import React, { useState, useEffect } from 'react'
import { CheckCircle, AlertCircle, XCircle, Info, Loader2 } from 'lucide-react'

/**
 * StatusBar Component
 * 
 * A thin status bar that displays messages in place of toast notifications.
 * Shows different types of messages with appropriate icons and colors.
 */
const StatusBar = ({ 
  message = "", 
  type = "info", // info, success, warning, error, loading
  isVisible = true,
  onDismiss = null
}) => {
  const [isAnimatingIn, setIsAnimatingIn] = useState(false)
  const [currentMessage, setCurrentMessage] = useState(message)
  const [currentType, setCurrentType] = useState(type)

  // Handle message changes with smooth transitions
  useEffect(() => {
    if (isVisible && message) {
      if (currentMessage !== message || currentType !== type) {
        // Briefly fade out, change content, then fade in
        setIsAnimatingIn(false)
        setTimeout(() => {
          setCurrentMessage(message)
          setCurrentType(type)
          setIsAnimatingIn(true)
        }, 150)
      } else {
        setIsAnimatingIn(true)
      }
    }
  }, [message, type, isVisible, currentMessage, currentType])

  if (!isVisible || !message) {
    return null
  }

  const getStatusIcon = () => {
    switch (currentType) {
      case 'success':
        return <CheckCircle className="h-3 w-3 text-green-600 dark:text-green-400 animate-pulse" />
      case 'warning':
        return <AlertCircle className="h-3 w-3 text-yellow-600 dark:text-yellow-400 animate-bounce" />
      case 'error':
        return <XCircle className="h-3 w-3 text-red-600 dark:text-red-400 animate-pulse" />
      case 'loading':
        return <Loader2 className="h-3 w-3 text-blue-600 dark:text-blue-400 animate-spin" />
      default:
        return <Info className="h-3 w-3 text-blue-600 dark:text-blue-400 animate-pulse" />
    }
  }

  const getStatusColors = () => {
    switch (currentType) {
      case 'success':
        return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700 text-green-800 dark:text-green-200'
      case 'warning':
        return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700 text-yellow-800 dark:text-yellow-200'
      case 'error':
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700 text-red-800 dark:text-red-200'
      case 'loading':
        return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700 text-blue-800 dark:text-blue-200'
      default:
        return 'bg-gray-50 dark:bg-gray-800/20 border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200'
    }
  }

  return (
    <div 
      className={`relative w-full border-b transform overflow-hidden ${
        isVisible 
          ? 'opacity-100 max-h-12' 
          : 'opacity-0 max-h-0 py-0'
      } ${getStatusColors()}`}
      style={{
        animation: isVisible && isAnimatingIn 
          ? 'statusBounceIn 0.5s ease-out forwards' 
          : !isVisible 
            ? 'statusSlideOut 0.3s ease-in forwards' 
            : 'none',
        transitionProperty: 'opacity, max-height, padding',
        transitionDuration: '300ms',
        transitionTimingFunction: 'ease-in-out'
      }}
    >
      {/* Progress bar for loading states */}
      {currentType === 'loading' && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-200 dark:bg-blue-700 overflow-hidden">
          <div 
            className="h-full w-full bg-gradient-to-r from-blue-500 via-blue-600 to-blue-500 dark:from-blue-400 dark:via-blue-500 dark:to-blue-400" 
            style={{
              animation: 'slide 2s ease-in-out infinite',
              backgroundSize: '200% 100%'
            }} 
          />
        </div>
      )}
      
      <div className={`px-3 py-1 transition-all duration-200 ease-in-out ${
        isAnimatingIn ? 'opacity-100 scale-100' : 'opacity-75 scale-95'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 min-w-0">
            <div className="transition-all duration-200 ease-in-out transform hover:scale-110">
              {getStatusIcon()}
            </div>
            <span className="text-xs font-medium truncate transition-all duration-200 ease-in-out">
              {currentMessage}
            </span>
          </div>
          
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="ml-2 flex-shrink-0 p-0.5 hover:opacity-70 hover:scale-110 transition-all duration-200 ease-in-out transform hover:rotate-90"
              title="Dismiss"
            >
              <XCircle className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default StatusBar 